import React, { useState, useEffect } from 'react';
import { Map, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { IoClose } from 'react-icons/io5';
import { getCurrentDeviceLocation } from '../../../hooks/location/useLocationState';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for primary color (red - primary-700: #a30026)
const primaryIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom icon for gray color
const grayIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-black.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

/**
 * Componente ExplorerModal
 * 
 * Modal con mapa interactivo para seleccionar ubicación manualmente
 * Permite al usuario explorar otras ubicaciones sin que Radar sepa si es real o explorada
 * 
 * @param {Object} props - Props del componente
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {Function} props.onConfirm - Función llamada al confirmar ubicación (lat, lng)
 * @param {Function} props.onDeactivate - Función llamada al desactivar Explorer
 * @param {Object} props.currentExplorerLocation - Ubicación actual del Explorer (si está activo)
 */
const ExplorerModal = ({ onClose, onConfirm, onDeactivate, currentExplorerLocation }) => {
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /**
   * Inicializa el mapa centrado en la ubicación actual del dispositivo o del Explorer
   */
  const initializeMap = async () => {
    try {
      if (currentExplorerLocation) {
        // Si Explorer está activo, usar la ubicación explorada
        console.log('[ExplorerModal] Initializing map with explorer location:', currentExplorerLocation);
        setMapCenter([currentExplorerLocation.lat, currentExplorerLocation.lng]);
        setSelectedLocation(currentExplorerLocation);
      } else {
        // Si Explorer está inactivo, usar la ubicación del dispositivo
        console.log('[ExplorerModal] Initializing map with device location');
        const deviceLoc = await getCurrentDeviceLocation();
        setMapCenter([deviceLoc.lat, deviceLoc.lng]);
      }
      setLoading(false);
    } catch (e) {
      console.error('[ExplorerModal] Error getting device location:', e);
      // Fallback: usar ubicación por defecto (ej: Buenos Aires)
      setMapCenter([-34.6037, -58.3816]);
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeMap();
  }, [currentExplorerLocation]);

  /**
   * Maneja el clic en el mapa para seleccionar ubicación
   */
  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    console.log('[ExplorerModal] Map clicked:', { lat, lng });
    setSelectedLocation({ lat, lng });
  };

  /**
   * Confirma la ubicación seleccionada
   */
  const handleConfirmLocation = async () => {
    if (!selectedLocation) {
      console.warn('[ExplorerModal] No location selected');
      return;
    }

    setSaving(true);
    try {
      console.log('[ExplorerModal] Confirming location:', selectedLocation);
      await onConfirm(selectedLocation.lat, selectedLocation.lng);
    } catch (e) {
      console.error('[ExplorerModal] Error confirming location:', e);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Desactiva el modo Explorer
   */
  const handleDeactivate = async () => {
    setSaving(true);
    try {
      console.log('[ExplorerModal] Deactivating explorer mode');
      await onDeactivate();
    } catch (e) {
      console.error('[ExplorerModal] Error deactivating explorer:', e);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Cancela y cierra el modal sin guardar cambios
   */
  const handleCancel = () => {
    console.log('[ExplorerModal] Cancelled');
    onClose();
  };
  
  /**
   * Verifica si la ubicación seleccionada es la misma que la actual del Explorer
   */
  const isSameAsCurrentExplorer = () => {
    if (!currentExplorerLocation || !selectedLocation) return false;
    return currentExplorerLocation.lat === selectedLocation.lat && 
           currentExplorerLocation.lng === selectedLocation.lng;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-neutral-800 rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-600 mb-4"></div>
            <p className="text-gray-50">Cargando mapa...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-neutral-800 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-neutral-700 flex justify-between items-center">
          <h2 className="text-xl text-white font-bold">Explorar ubicación</h2>
          <button 
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded"
            aria-label="Cerrar"
          >
            <IoClose size={24} />
          </button>
        </div>
        
        {/* Mapa */}
        <div className="flex-1 min-h-[400px] relative p-1">
          {mapCenter && (
            <Map
              center={mapCenter}
              zoom={13}
              style={{ height: '400px', width: '100%' }}
              onClick={handleMapClick}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* Pin de ubicación actual del dispositivo */}
              <Marker 
                position={mapCenter} 
                icon={selectedLocation ? grayIcon : primaryIcon}
              />
              {/* Pin de ubicación seleccionada por el usuario */}
              {selectedLocation && (
                <Marker 
                  position={[selectedLocation.lat, selectedLocation.lng]} 
                  icon={primaryIcon}
                />
              )}
            </Map>
          )}
        </div>
        
        {/* Info de ubicación seleccionada */}
        {/* {selectedLocation && (
          <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
            Ubicación seleccionada: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
          </div>
        )} */}
        
        {/* Footer */}
        <div className="p-4 border-t border-neutral-700 flex gap-3">
          {currentExplorerLocation && isSameAsCurrentExplorer() ? (
            // Si Explorer está activo y la ubicación seleccionada es la misma, mostrar botón de desactivar
            <button
              onClick={handleDeactivate}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Desactivando...' : 'Desactivar ubicación'}
            </button>
          ) : (
            // Si Explorer está inactivo o la ubicación es diferente, mostrar botones de cancelar/confirmar
            <>
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 border border-neutral-600 rounded hover:bg-neutral-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmLocation}
                disabled={!selectedLocation || saving}
                className="flex-1 px-4 py-2 bg-neutral-600 text-white rounded hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Guardando...' : 'Confirmar ubicación'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExplorerModal;
